import { useCallback, useEffect, useMemo, useState } from 'react';

import type { NudgeRecommendationDetails } from '@atlassian/convo-ai-api';

import { fetchNudgeRecommendations } from './fetchNudgeRecommendations';

type useFetchNudgeRecommendationsReturnType = {
	error?: Error;
	loading: boolean;
	recommendations: NudgeRecommendationDetails[] | undefined;
};

export const useFetchNudgeRecommendations = (
	triggerId: string,
	shouldTrigger: boolean,
	pageUrl: string,
	product: string,
): useFetchNudgeRecommendationsReturnType => {
	const [error, setError] = useState<Error | undefined>();
	const [loading, setLoading] = useState<boolean>(true);
	const [recommendations, setRecommendations] = useState<NudgeRecommendationDetails[] | undefined>(
		undefined,
	);
	const [prevUrl, setPrevUrl] = useState<string | undefined>();

	const makeNudgeRecommendationsRequest = useCallback(async () => {
		if (product !== 'confluence') { // Temporary: only Confluence is migrated to new nudging system
			setLoading(false);
			return;
		}

		if (
			!product || // invalid request parameter
			!shouldTrigger || // unsupported, no nudges to recommend
			pageUrl === prevUrl // prevent duplicate page requests
		) {
			setLoading(false);
			return;
		}

		// Begin new request under valid conditions
		if (!loading && product && shouldTrigger && pageUrl !== prevUrl) {
			setLoading(true);
			setError(undefined);
			setRecommendations(undefined);
			return;
		}

		try {
			const response = await fetchNudgeRecommendations({ triggerId, pageUrl, product });
			setRecommendations(
				response?.triggers.flatMap((trigger) =>
					trigger.recommendations.map((recommendation) => recommendation.recommendation_details),
				),
			);
			setError(undefined);
		} catch (error: unknown) {
			setRecommendations(undefined);
			setError(error as Error);
		} finally {
			setPrevUrl(pageUrl);
			setLoading(false);
		}
	}, [loading, pageUrl, prevUrl, product, shouldTrigger, triggerId]);

	useEffect(() => {
		void makeNudgeRecommendationsRequest();
	}, [loading, makeNudgeRecommendationsRequest]);

	return useMemo(
		() => ({
			recommendations: loading ? undefined : recommendations,
			loading,
			error,
		}),
		[loading, error, recommendations],
	);
};
